import React, { useState } from 'react';
import { BRAND, CONTACT_INFO, NAV_LINKS } from '../data/mockData';
import { ArrowRight, ArrowUpRight, MapPin, Phone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../api';

const ContactFooter = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    service: '',
    message: '',
    website: '', // honeypot — must remain empty
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      // Capture lightweight source: page path + utm_source if present
      let source = window.location.pathname || '/';
      try {
        const params = new URLSearchParams(window.location.search);
        const utm = params.get('utm_source');
        if (utm) source = `${source} · utm:${utm}`;
        if (document.referrer && !document.referrer.includes(window.location.host)) {
          const ref = new URL(document.referrer).hostname;
          if (ref) source = `${source} · ref:${ref}`;
        }
      } catch (_) { /* ignore */ }
      await api.submitContact({ ...formData, source });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      setFormData({ firstName: '', lastName: '', company: '', email: '', service: '', message: '', website: '' });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setErrorMsg(typeof detail === 'string' ? detail : 'Too many submissions. Please try again in a few minutes.');
      } else {
        setErrorMsg('Could not send your message. Please try again or email us directly.');
      }
      console.error('Contact form error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Contact Section */}
      <section id="contact" className="bg-[#FDFEFA] py-14 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left - Headline */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-[#242326] text-3xl md:text-5xl lg:text-[56px] font-light leading-tight tracking-tight font-['Inter'] mb-6">
                Get in touch
              </h2>
              <p className="text-[#4A4A48] text-lg font-['Lato'] font-light mb-10">
                And see how we'd drive results for you
              </p>

              {/* Contact Details */}
              <div className="space-y-8" data-testid="contact-offices">
                {CONTACT_INFO.offices.map((office) => (
                  <div key={office.city} data-testid={`contact-office-${office.city.toLowerCase()}`}>
                    <h3 className="text-[#242326] font-medium font-['Inter'] text-lg mb-3">
                      {office.city}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3 text-[#4A4A48] font-['Lato'] text-sm">
                        <MapPin className="w-4 h-4 mt-0.5 text-[#83BF42] flex-shrink-0" />
                        {office.address}
                      </div>
                      <a
                        href={office.tel}
                        data-testid={`contact-phone-${office.city.toLowerCase()}`}
                        className="flex items-center gap-3 text-[#4A4A48] font-['Lato'] text-sm hover:text-[#83BF42] transition-colors"
                      >
                        <Phone className="w-4 h-4 text-[#83BF42]" />
                        {office.phone}
                      </a>
                    </div>
                  </div>
                ))}
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="inline-flex items-center gap-3 text-[#4A4A48] font-['Lato'] text-sm hover:text-[#83BF42] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#83BF42]" />
                  {CONTACT_INFO.email}
                </a>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {submitted ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-[#83BF42]/10 flex items-center justify-center mx-auto mb-4">
                      <ArrowUpRight className="w-8 h-8 text-[#83BF42]" />
                    </div>
                    <h3 className="text-[#242326] text-2xl font-['Inter'] font-medium mb-2">
                      Message sent!
                    </h3>
                    <p className="text-[#4A4A48] font-['Lato']">We'll be in touch soon.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {/* Honeypot — visually hidden but present in DOM. Real users never fill this. */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                    data-testid="contact-honeypot"
                  />

                  {errorMsg && (
                    <div data-testid="contact-error" className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-['Inter']">
                      {errorMsg}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[#242326] text-xs font-medium font-['Inter'] uppercase tracking-wider mb-2 block">
                        First Name *
                      </label>
                      <input
                        required
                        type="text"
                        name="firstName"
                        data-testid="contact-firstname"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-[#242326]/10 bg-white text-[#242326] font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="text-[#242326] text-xs font-medium font-['Inter'] uppercase tracking-wider mb-2 block">
                        Last Name *
                      </label>
                      <input
                        required
                        type="text"
                        name="lastName"
                        data-testid="contact-lastname"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-[#242326]/10 bg-white text-[#242326] font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[#242326] text-xs font-medium font-['Inter'] uppercase tracking-wider mb-2 block">
                      Company Name *
                    </label>
                    <input
                      required
                      type="text"
                      name="company"
                      data-testid="contact-company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-[#242326]/10 bg-white text-[#242326] font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                      placeholder="Your company"
                    />
                  </div>

                  <div>
                    <label className="text-[#242326] text-xs font-medium font-['Inter'] uppercase tracking-wider mb-2 block">
                      Company Email *
                    </label>
                    <input
                      required
                      type="email"
                      name="email"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-[#242326]/10 bg-white text-[#242326] font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                      placeholder="you@company.com"
                    />
                  </div>

                  <div>
                    <label className="text-[#242326] text-xs font-medium font-['Inter'] uppercase tracking-wider mb-2 block">
                      How Can We Support You?
                    </label>
                    <select
                      name="service"
                      data-testid="contact-service"
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-[#242326]/10 bg-white text-[#4A4A48] font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors appearance-none"
                    >
                      <option value="">Select a service</option>
                      <option value="brand-strategy">Brand Strategy</option>
                      <option value="media-planning">Media Planning & Buying</option>
                      <option value="adtech">Adtech Consultancy</option>
                      <option value="creative">Creative Production</option>
                      <option value="insights">Market Insights</option>
                      <option value="programmatic">Programmatic Yield</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[#242326] text-xs font-medium font-['Inter'] uppercase tracking-wider mb-2 block">
                      Tell Us More
                    </label>
                    <textarea
                      name="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-[#242326]/10 bg-white text-[#242326] font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#242326] text-white text-[15px] font-['Inter'] font-light hover:bg-[#83BF42] transition-all duration-300 group disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#242326] py-16 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
            {/* Logo & Tagline */}
            <div className="md:col-span-2">
              <img
                src={BRAND.logoFull}
                alt="Grouth"
                className="h-12 md:h-14 w-auto brightness-0 invert mb-6"
              />
              <p className="text-white/50 font-['Lato'] text-sm leading-relaxed max-w-sm mb-6">
                Your business is ours too. We apply our blueprint of successful growth and tailor it to your brand's needs.
              </p>
              <div className="flex flex-wrap gap-4 text-white/50 text-xs font-['Lato']">
                <a
                  href="/insights"
                  data-testid="footer-insights"
                  className="hover:text-[#83BF42] transition-colors"
                >
                  Insights
                </a>
                <span className="text-white/15">·</span>
                <a
                  href="/newsletter"
                  data-testid="footer-newsletter"
                  className="hover:text-[#83BF42] transition-colors"
                >
                  Newsletter archive
                </a>
                <span className="text-white/15">·</span>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/rss.xml`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-rss"
                  className="hover:text-[#83BF42] transition-colors"
                >
                  RSS
                </a>
              </div>
            </div>

            {/* Navigation Links */}
            <div>
              <h4 className="text-white text-sm font-medium font-['Inter'] uppercase tracking-wider mb-4">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {NAV_LINKS.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-white/50 text-sm font-['Lato'] hover:text-[#83BF42] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#contact"
                    className="text-white/50 text-sm font-['Lato'] hover:text-[#83BF42] transition-colors"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white text-sm font-medium font-['Inter'] uppercase tracking-wider mb-4">
                Get in Touch
              </h4>
              <ul className="space-y-3" data-testid="footer-contact-list">
                <li>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="text-white/50 text-sm font-['Lato'] hover:text-[#83BF42] transition-colors"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </li>
                {CONTACT_INFO.offices.map((office) => (
                  <li
                    key={office.city}
                    className="text-white/50 text-sm font-['Lato']"
                    data-testid={`footer-office-${office.city.toLowerCase()}`}
                  >
                    <span className="text-white/70">{office.city}</span>
                    <span className="mx-2 text-white/20">·</span>
                    <a
                      href={office.tel}
                      className="hover:text-[#83BF42] transition-colors"
                    >
                      {office.phone}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-xs font-['Lato']" data-testid="footer-copyright">
              {"\u00A9"} {new Date().getFullYear()} Grouth Marketing Limited. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-6">
              <a
                href="/privacy"
                data-testid="footer-privacy"
                className="text-white/30 text-xs font-['Lato'] hover:text-white/60 transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                data-testid="footer-terms"
                className="text-white/30 text-xs font-['Lato'] hover:text-white/60 transition-colors"
              >
                Terms &amp; Conditions
              </a>
              <a
                href="/cookie-policy"
                data-testid="footer-cookie-policy"
                className="text-white/30 text-xs font-['Lato'] hover:text-white/60 transition-colors"
              >
                Cookie Policy
              </a>
              <button
                type="button"
                data-testid="footer-cookie-settings"
                onClick={() => {
                  // Trigger Usercentrics CMP if loaded; otherwise fall back to the cookie-policy page.
                  try {
                    if (window.UC_UI && typeof window.UC_UI.showSecondLayer === 'function') {
                      window.UC_UI.showSecondLayer();
                      return;
                    }
                  } catch (_) { /* no-op */ }
                  window.location.assign('/cookie-policy');
                }}
                className="text-white/30 text-xs font-['Lato'] hover:text-white/60 transition-colors"
              >
                Cookie Settings
              </button>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default ContactFooter;
